// Report page: read-only aggregates over MY OWN payment requests (apiMyRequestReport) —
// headline stat tiles, a labeled status breakdown, a single-hue monthly trend with
// tap-to-inspect, and top budget-item / vendor bar lists. Filterable by financial year
// and request type. Identity is never color-alone: every colored mark sits beside its
// text label, and values stay in ink tokens.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useInitData } from "@/lib/init-data";
import {
  fetchMyRequestFilterOptions,
  formatAmount,
  statusMeta,
  type FilterOption,
} from "@/lib/payreq";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

interface ReportStatus {
  status: string;
  count: number;
  amount: number;
}
interface ReportMonth {
  month: string; // "yyyy-MM"
  amount: number;
  count: number;
}
interface ReportRow {
  name: string | null;
  department?: string | null;
  amount: number;
  count: number;
}
interface MyRequestReport {
  totals: { count: number; totalRequested: number; totalPaid: number; totalBalance: number };
  statuses: ReportStatus[];
  monthly: ReportMonth[];
  byBudget: ReportRow[];
  byVendor: ReportRow[];
}

/** Fixed display order + solid mark colors for the status breakdown (labels always shown). */
const STATUS_ORDER: { key: string; dot: string }[] = [
  { key: "PENDING_APPROVAL", dot: "bg-amber-500" },
  { key: "APPROVED", dot: "bg-blue-500" },
  { key: "PROCESSING", dot: "bg-violet-500" },
  { key: "ON_HOLD", dot: "bg-red-500" },
  { key: "PARTIALLY_PAID", dot: "bg-teal-500" },
  { key: "PAID", dot: "bg-green-600" },
];

function monthLabel(m: string, long = false): string {
  const [y, mm] = m.split("-").map(Number);
  const d = new Date(y, (mm ?? 1) - 1, 1);
  return d.toLocaleDateString("en-IN", long ? { month: "short", year: "numeric" } : { month: "short" });
}

export default function ReportsPage() {
  const { token } = useAuth();
  const initData = useInitData();

  const [years, setYears] = useState<FilterOption[]>([]);
  const [yearId, setYearId] = useState<number | null>(null);
  const [typeId, setTypeId] = useState<number | null>(null);
  const [operationUnitId, setOperationUnitId] = useState<number | null>(null);

  // Location is mandatory (the whole system is location-scoped): preselect the first OU.
  useEffect(() => {
    if (operationUnitId == null && initData.myOperationUnits.length > 0) {
      setOperationUnitId(initData.myOperationUnits[0].id);
    }
  }, [operationUnitId, initData.myOperationUnits]);
  const [report, setReport] = useState<MyRequestReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetchMyRequestFilterOptions(token)
      .then((o) => setYears(o.financialYears))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token || operationUnitId == null) return;
    const ctrl = new AbortController();
    setLoading(true);
    const body: Record<string, unknown> = { operationUnitId };
    if (yearId != null) body.financialYearId = yearId;
    if (typeId != null) body.reqTypeId = typeId;
    apiFetch<MyRequestReport>("/api/myPaymentRequest/apiMyRequestReport", {
      token,
      method: "POST",
      body,
      signal: ctrl.signal,
    })
      .then((d) => {
        setReport(d);
        setSelectedMonth(d.monthly.length > 0 ? d.monthly[d.monthly.length - 1].month : null);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        toast.error(e instanceof ApiError ? e.message : "Could not load the report.");
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [token, yearId, typeId, operationUnitId]);

  const typeOptions = [
    { value: "", label: "All types" },
    { value: initData.reqTypePaymentRequestId ?? "541", label: "Vendor payment" },
    { value: initData.reqTypeInternalTransferId ?? "542", label: "Internal transfer" },
  ];

  const months = useMemo(() => (report?.monthly ?? []).slice(-12), [report]);
  const maxMonth = Math.max(1, ...months.map((m) => m.amount));
  const selected = months.find((m) => m.month === selectedMonth) ?? null;

  const statuses = useMemo(() => {
    const byKey = new Map((report?.statuses ?? []).map((s) => [s.status, s]));
    return STATUS_ORDER.map((o) => ({ ...o, ...(byKey.get(o.key) ?? { count: 0, amount: 0 }) })).filter(
      (s) => s.count > 0
    );
  }, [report]);
  const statusTotal = Math.max(1, statuses.reduce((a, s) => a + s.count, 0));

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* Filters — one row above the charts */}
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2 flex flex-col gap-1.5">
          <Label>Location</Label>
          <Select
            items={initData.myOperationUnits.map((o) => ({ value: String(o.id), label: o.name ?? String(o.id) }))}
            value={operationUnitId != null ? String(operationUnitId) : ""}
            onValueChange={(v) => setOperationUnitId(v ? Number(v) : null)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select location…" />
            </SelectTrigger>
            <SelectContent>
              {initData.myOperationUnits.map((o) => (
                <SelectItem key={o.id} value={String(o.id)}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Financial year</Label>
          <Select
            items={[{ value: "", label: "All years" }, ...years.map((y) => ({ value: String(y.id), label: y.name ?? String(y.id) }))]}
            value={yearId != null ? String(yearId) : ""}
            onValueChange={(v) => setYearId(v ? Number(v) : null)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All years</SelectItem>
              {years.map((y) => (
                <SelectItem key={y.id} value={String(y.id)}>
                  {y.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Type</Label>
          <Select
            items={typeOptions}
            value={typeId != null ? String(typeId) : ""}
            onValueChange={(v) => setTypeId(v ? Number(v) : null)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {typeOptions.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading || !report ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      ) : report.totals.count === 0 ? (
        <div className="flex flex-col items-center gap-1 py-16 text-center">
          <p className="text-sm font-medium">Nothing to report</p>
          <p className="text-xs text-muted-foreground">
            No payment requests match the selected filters.
          </p>
        </div>
      ) : (
        <>
          {/* Headline stat tiles */}
          <div className="grid grid-cols-2 gap-2">
            <StatTile label="Requested" value={formatAmount(report.totals.totalRequested)} />
            <StatTile label="Paid" value={formatAmount(report.totals.totalPaid)} />
            <StatTile label="Balance" value={formatAmount(report.totals.totalBalance)} />
            <StatTile label="Requests" value={String(report.totals.count)} />
          </div>

          {/* Status breakdown — segmented bar + labeled legend rows */}
          {statuses.length > 0 && (
            <Card size="sm">
              <CardHeader>
                <CardTitle className="text-sm">By status</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <div className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full">
                  {statuses.map((s) => (
                    <div
                      key={s.key}
                      className={cn("h-full", s.dot)}
                      style={{ width: `${(s.count / statusTotal) * 100}%` }}
                      aria-label={`${statusMeta(s.key).label}: ${s.count}`}
                    />
                  ))}
                </div>
                <ul className="flex flex-col gap-1.5">
                  {statuses.map((s) => (
                    <li key={s.key} className="flex items-center gap-2 text-sm">
                      <span className={cn("size-2.5 shrink-0 rounded-full", s.dot)} />
                      <span className="min-w-0 flex-1 truncate capitalize">
                        {statusMeta(s.key).label}
                      </span>
                      <span className="text-xs text-muted-foreground">{s.count}</span>
                      <span className="w-24 text-right text-sm font-medium tabular-nums">
                        {formatAmount(s.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Monthly trend — single hue, tap a bar to inspect */}
          {months.length > 0 && (
            <Card size="sm">
              <CardHeader>
                <CardTitle className="text-sm">Requested by month</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                <p className="text-xs text-muted-foreground" aria-live="polite">
                  {selected
                    ? `${monthLabel(selected.month, true)} · ${formatAmount(selected.amount)} · ${selected.count} request${selected.count === 1 ? "" : "s"}`
                    : "Tap a bar for details."}
                </p>
                <div className="flex h-32 items-end gap-1">
                  {months.map((m) => (
                    <button
                      key={m.month}
                      type="button"
                      className="group flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
                      aria-label={`${monthLabel(m.month, true)}: ${formatAmount(m.amount)}`}
                      onClick={() => setSelectedMonth(m.month)}
                    >
                      <div
                        className={cn(
                          "w-full max-w-7 rounded-t-[4px] transition-colors",
                          m.month === selectedMonth ? "bg-primary" : "bg-primary/45 group-hover:bg-primary/70"
                        )}
                        style={{ height: `${Math.max(2, (m.amount / maxMonth) * 100)}%` }}
                      />
                      <span className="text-[10px] leading-none text-muted-foreground">
                        {monthLabel(m.month)}
                      </span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <BarListCard
            title="Top budget items"
            rows={report.byBudget.map((r) => ({
              label: r.name ?? "—",
              sub: r.department ?? undefined,
              amount: r.amount,
              count: r.count,
            }))}
          />
          <BarListCard
            title="Top vendors"
            rows={report.byVendor.map((r) => ({
              label: r.name ?? "—",
              amount: r.amount,
              count: r.count,
            }))}
          />
        </>
      )}
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <Card size="sm">
      <CardContent className="flex flex-col gap-0.5">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="truncate text-lg font-semibold tabular-nums">{value}</span>
      </CardContent>
    </Card>
  );
}

function BarListCard({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; sub?: string; amount: number; count: number }[];
}) {
  if (rows.length === 0) return null;
  const max = Math.max(1, ...rows.map((r) => r.amount));
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2.5">
        {rows.map((r, i) => (
          <div key={`${r.label}-${i}`} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="min-w-0 truncate">
                {r.label}
                {r.sub && <span className="text-xs text-muted-foreground"> · {r.sub}</span>}
              </span>
              <span className="shrink-0 font-medium tabular-nums">{formatAmount(r.amount)}</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary/70"
                style={{ width: `${(r.amount / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
